import { useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Textarea } from "@/components/ui/textarea";
import { Copy, Loader2, ShieldCheck, MailCheck } from "lucide-react";

import { toast } from "sonner";
import { SHARE_FIELDS, SHARE_PRESETS } from "@/lib/intakeShareFields";

const APP_URL = (import.meta.env.VITE_PUBLIC_APP_URL || "https://dashboard.creativeinvestments.africa").replace(/\/$/, "");

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  leadId: string;
  leadTitle?: string;
  submissions: Array<{ id: string; full_name: string; flag_unexpected?: boolean | null; flag_duplicate_of?: string | null; review_status?: string | null }>;
  preselectedIds: string[];
}

type PresetKey = keyof typeof SHARE_PRESETS;

export function ShareSubmissionsDialog({ open, onOpenChange, leadId, leadTitle, submissions, preselectedIds }: Props) {
  const qc = useQueryClient();
  const [recipientName, setRecipientName] = useState("");
  const [recipientEmail, setRecipientEmail] = useState("");
  const [purpose, setPurpose] = useState("");
  const [preset, setPreset] = useState<PresetKey>("hotel");
  const [fields, setFields] = useState<string[]>(SHARE_PRESETS.hotel.fields);
  const [maskPassport, setMaskPassport] = useState(true);
  const [allowPhotoDownload, setAllowPhotoDownload] = useState(false);
  const [expiryDays, setExpiryDays] = useState("7");
  const [ids, setIds] = useState<string[]>(preselectedIds);
  const [createdUrl, setCreatedUrl] = useState<string | null>(null);
  const [sendEmail, setSendEmail] = useState(true);
  const [message, setMessage] = useState("");
  const [emailStatus, setEmailStatus] = useState<"sent" | "failed" | "skipped" | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);


  useMemo(() => { setIds(preselectedIds); }, [preselectedIds.join(",")]);

  const groups = useMemo(() => {
    const map = new Map<string, typeof SHARE_FIELDS>();
    for (const f of SHARE_FIELDS) {
      map.set(f.group, [...(map.get(f.group) || []), f]);
    }
    return Array.from(map.entries());
  }, []);

  function applyPreset(key: PresetKey) {
    setPreset(key);
    const p = SHARE_PRESETS[key];
    setFields(p.fields);
    setMaskPassport(p.maskPassport);
    setAllowPhotoDownload(p.allowPhotoDownload);
  }

  function toggleField(key: string) {
    setPreset("custom");
    setFields((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  }

  const createMut = useMutation({
    mutationFn: async () => {
      const { data: auth } = await supabase.auth.getUser();
      const expires_at = new Date(Date.now() + Number(expiryDays) * 86400000).toISOString();
      const { data, error } = await (supabase as any)
        .from("intake_share_links")
        .insert({
          lead_id: leadId,
          submission_ids: ids,
          recipient_name: recipientName.trim(),
          recipient_email: recipientEmail.trim().toLowerCase(),
          purpose: purpose.trim(),
          shared_fields: fields,
          mask_passport: maskPassport,
          allow_photo_download: allowPhotoDownload,
          expires_at,
          created_by: auth?.user?.id ?? null,
          invite_message: sendEmail && message.trim() ? message.trim() : null,
        })
        .select("id, token")
        .single();
      if (error) throw error;

      let emailed: "sent" | "failed" | "skipped" = "skipped";
      let emailErr: string | null = null;
      if (sendEmail) {
        const { data: res, error: fnErr } = await supabase.functions.invoke(
          "send-intake-share-invite",
          { body: { shareId: data.id, message: message.trim() || undefined } },
        );
        if (fnErr || (res as any)?.error) {
          emailed = "failed";
          emailErr = (res as any)?.error || fnErr?.message || "Email could not be sent";
        } else {
          emailed = "sent";
        }
      }
      return { token: data.token as string, emailed, emailErr };
    },
    onSuccess: ({ token, emailed, emailErr }) => {
      const url = `${APP_URL}/intake-share/${token}`;
      setCreatedUrl(url);
      setEmailStatus(emailed);
      setEmailError(emailErr);
      navigator.clipboard?.writeText(url).catch(() => {});
      qc.invalidateQueries({ queryKey: ["intake-shares", leadId] });
      if (emailed === "sent") toast.success(`Secure link emailed to ${recipientEmail}`);
      else if (emailed === "failed") toast.error("Link created, but the email could not be sent");
      else toast.success("Secure share link created and copied");
    },
    onError: (e: any) => toast.error(e?.message || "Failed to create share link"),
  });

  function reset() {
    setCreatedUrl(null);
    setRecipientName("");
    setRecipientEmail("");
    setPurpose("");
    setMessage("");
    setEmailStatus(null);
    setEmailError(null);
    applyPreset("hotel");
  }

  const canCreate =
    recipientName.trim().length > 1 &&
    recipientEmail.includes("@") &&
    purpose.trim().length > 2 &&

    ids.length > 0 &&
    fields.length > 0;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Share submissions securely</DialogTitle>
          <DialogDescription>
            {leadTitle ? <>Sharing details from <strong>{leadTitle}</strong>. </> : null}
            The recipient verifies with a one-time code emailed to them — no account or password. Only the fields you tick are shared.
          </DialogDescription>
        </DialogHeader>

        {(() => {
          const flagged = submissions.filter(
            (s) => (s.review_status || "open") === "open" && (s.flag_unexpected || s.flag_duplicate_of) && ids.includes(s.id),
          );
          if (!flagged.length) return null;
          return (
            <div className="rounded-md border border-amber-500 bg-amber-50 p-3 text-sm text-amber-800">
              {flagged.length} selected {flagged.length === 1 ? "person is" : "people are"} still flagged for review
              {leadTitle ? <> on {leadTitle}</> : null} ({flagged.map((s) => s.full_name).join(", ")}). Check they belong
              to this programme before sharing their details.
            </div>
          );
        })()}


        {createdUrl ? (
          <div className="space-y-3">
            <Alert>
              {emailStatus === "sent" ? <MailCheck className="h-4 w-4" /> : <ShieldCheck className="h-4 w-4" />}
              <AlertDescription className="text-xs">
                {emailStatus === "sent"
                  ? `The secure link was emailed to ${recipientEmail}. They must enter that exact email address to receive their access code.`
                  : emailStatus === "failed"
                  ? `The link was created but the email failed to send${emailError ? ` (${emailError})` : ""}. Copy the link below and send it manually, or use Resend from the shares list.`
                  : `Send this link to ${recipientEmail || "the recipient"}. They must enter that exact email address to receive their access code.`}
              </AlertDescription>
            </Alert>

            <div className="flex gap-2">
              <Input readOnly value={createdUrl} className="font-mono text-xs" />
              <Button variant="outline" size="icon" onClick={() => { navigator.clipboard.writeText(createdUrl); toast.success("Copied"); }}>
                <Copy className="h-4 w-4" />
              </Button>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={reset}>Create another</Button>
              <Button onClick={() => { reset(); onOpenChange(false); }}>Done</Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-5">
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label>Recipient name</Label>
                <Input value={recipientName} onChange={(e) => setRecipientName(e.target.value)} placeholder="Protea Hotel Sea Point" />
              </div>
              <div>
                <Label>Recipient email</Label>
                <Input type="email" value={recipientEmail} onChange={(e) => setRecipientEmail(e.target.value)} placeholder="reservations@hotel.com" />
              </div>
              <div className="sm:col-span-2">
                <Label>Purpose (shown to the recipient)</Label>
                <Input value={purpose} onChange={(e) => setPurpose(e.target.value)} placeholder="Rooming list and catering for Kurus English cohort, Sept 2026" />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label>Preset</Label>
                <Select value={preset} onValueChange={(v) => applyPreset(v as PresetKey)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(SHARE_PRESETS).map(([k, p]) => (
                      <SelectItem key={k} value={k}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">{SHARE_PRESETS[preset].description}</p>
              </div>
              <div>
                <Label>Link expires in</Label>
                <Select value={expiryDays} onValueChange={setExpiryDays}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">24 hours</SelectItem>
                    <SelectItem value="7">7 days</SelectItem>
                    <SelectItem value="30">30 days</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label className="mb-2 block">Submissions to include ({ids.length}/{submissions.length})</Label>
              <div className="flex items-center gap-2 mb-2">
                <Button type="button" variant="outline" size="sm" onClick={() => setIds(submissions.map((s) => s.id))}>Select all</Button>
                <Button type="button" variant="ghost" size="sm" onClick={() => setIds([])}>Clear</Button>
              </div>
              <div className="border rounded-md p-2 max-h-32 overflow-y-auto space-y-1">
                {submissions.map((s) => (
                  <label key={s.id} className="flex items-center gap-2 text-sm">
                    <Checkbox
                      checked={ids.includes(s.id)}
                      onCheckedChange={(c) => setIds((prev) => (c ? [...prev, s.id] : prev.filter((i) => i !== s.id)))}
                    />
                    {s.full_name}
                  </label>
                ))}
              </div>
            </div>

            <div>
              <Label className="mb-2 block">Fields to share</Label>
              <div className="grid sm:grid-cols-2 gap-4">
                {groups.map(([group, gf]) => (
                  <div key={group} className="space-y-1">
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">{group}</p>
                    {gf.map((f) => (
                      <label key={f.key} className="flex items-center gap-2 text-sm">
                        <Checkbox checked={fields.includes(f.key)} onCheckedChange={() => toggleField(f.key)} />
                        <span>{f.label}</span>
                        {f.sensitive && <Badge variant="outline" className="text-[10px] text-destructive border-destructive">sensitive</Badge>}
                      </label>
                    ))}
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-3 border-t pt-3">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <Label htmlFor="mask-pp">Mask passport number</Label>
                  <p className="text-xs text-muted-foreground">Shows only the last 4 digits.</p>
                </div>
                <Switch id="mask-pp" checked={maskPassport} onCheckedChange={setMaskPassport} />
              </div>
              <div className="flex items-center justify-between gap-4">
                <div>
                  <Label htmlFor="allow-dl">Allow photo download</Label>
                  <p className="text-xs text-muted-foreground">Off means photos can be viewed in-page only.</p>
                </div>
                <Switch id="allow-dl" checked={allowPhotoDownload} onCheckedChange={setAllowPhotoDownload} />
              </div>
            </div>

            <div className="space-y-3 border-t pt-3">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <Label htmlFor="send-email">Email the link to the recipient</Label>
                  <p className="text-xs text-muted-foreground">Sends the secure link straight from the system — no data is included in the email.</p>
                </div>
                <Switch id="send-email" checked={sendEmail} onCheckedChange={setSendEmail} />
              </div>
              {sendEmail && (
                <div>
                  <Label>Message to recipient (optional)</Label>
                  <Textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    rows={3}
                    maxLength={800}
                    placeholder="Hi team, please find the rooming list details for our group arriving 12 September."
                  />
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button disabled={!canCreate || createMut.isPending} onClick={() => createMut.mutate()}>
                {createMut.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                {sendEmail ? "Create & send" : "Create secure link"}
              </Button>
            </DialogFooter>

          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
